const ALLOWED_META_HOSTNAMES = [
  "neetsupport.com",
  "counselling.neetsupport.com",
  "portal.neetsupport.com",
  "service.neetsupport.com",
  "livewebinar.neetsupport.com",
];

const BLOCKED_HOSTNAME_PATTERNS = [
  /localhost/i,
  /translate\.goog$/i,
  /staging/i,
  /preview/i,
  /test/i,
  /vercel\.app$/i,
  /ngrok/i,
];

const META_ROOT_DOMAIN = "https://neetsupport.com";
const DEFAULT_LEAD_VALUE = 49;
const DEFAULT_LEAD_CURRENCY = "INR";

const META_EVENT_ALIASES = {
  addpaymentinfo: "AddPaymentInfo",
  add_payment_info: "AddPaymentInfo",
  "add-payment-info": "AddPaymentInfo",
  search: "Search",
  viewcontent: "ViewContent",
  view_content: "ViewContent",
  "view-content": "ViewContent",
  contact: "Contact",
  submitapplication: "SubmitApplication",
  submit_application: "SubmitApplication",
  "submit-application": "SubmitApplication",
  initiatecheckout: "InitiateCheckout",
  initiate_checkout: "InitiateCheckout",
  "initiate-checkout": "InitiateCheckout",
  customizeproduct: "CustomizeProduct",
  customiseproduct: "CustomizeProduct",
  customize_product: "CustomizeProduct",
  customise_product: "CustomizeProduct",
  completeregistration: "CompleteRegistration",
  complete_registration: "CompleteRegistration",
  "complete-registration": "CompleteRegistration",
  findlocation: "FindLocation",
  find_location: "FindLocation",
  "find-location": "FindLocation",
  lead: "Lead",
  purchase: "Purchase",
  starttrial: "StartTrial",
  start_trial: "StartTrial",
  "start-trial": "StartTrial",
};

const isIsoCurrency = (currency) => /^[A-Z]{3}$/.test(String(currency || ""));

const normalizePhone = (phone = "") => {
  const digits = String(phone).replace(/\D/g, "");
  if (!digits) return "";

  if (digits.length === 10) return `91${digits}`;
  if (digits.length > 10) return digits;

  return "";
};

const normalizeMetaEventName = (eventName = "Lead") => {
  const rawName = String(eventName || "Lead").trim();
  const compactName = rawName.toLowerCase().replace(/[^a-z0-9]+/g, "");

  return META_EVENT_ALIASES[compactName] || rawName.replace(/\s+/g, "");
};

export const isAllowedMetaHostname = (hostname = "") => {
  if (!hostname) return false;

  const host = String(hostname).toLowerCase();

  if (BLOCKED_HOSTNAME_PATTERNS.some((pattern) => pattern.test(host))) {
    return false;
  }

  return ALLOWED_META_HOSTNAMES.includes(host);
};

export const normalizeEventSourceUrl = (rawUrlOrPath = "/thank-you") => {
  try {
    const url =
      rawUrlOrPath.startsWith("http://") || rawUrlOrPath.startsWith("https://")
        ? new URL(rawUrlOrPath)
        : new URL(rawUrlOrPath, META_ROOT_DOMAIN);

    const pathname = url.pathname || "/thank-you";
    const search = url.search || "";

    return `${META_ROOT_DOMAIN}${pathname}${search}`;
  } catch {
    return `${META_ROOT_DOMAIN}/thank-you`;
  }
};

const getCookieValue = (name) => {
  if (typeof document === "undefined") return "";

  const escaped = name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
};

const buildCapiEndpoint = () => {
  const base = String(process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_BACKEND || "").trim();

  if (!base) return "";

  const normalizedBase = base.replace(/\/$/, "");
  if (
    normalizedBase.endsWith("/events") ||
    normalizedBase.endsWith("/track-event") ||
    normalizedBase.endsWith("/track-purchase")
  ) {
    return normalizedBase;
  }

  if (normalizedBase.endsWith("/conversion-api") || normalizedBase.endsWith("/meta-capi")) {
    return `${normalizedBase}/events`;
  }

  return `${normalizedBase}/events`;
};

const markEventSent = (eventName, eventId, dedupeKey) => {
  try {
    const storageKeyPrefix = `meta_${String(eventName || "event").toLowerCase()}`;

    if (typeof window !== "undefined") {
      window.__metaEventIds = window.__metaEventIds || new Set();
      window.__metaEventIds.add(`${storageKeyPrefix}:${eventId}`);
    }

    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(`${storageKeyPrefix}_event_${eventId}`, "1");
      if (dedupeKey) {
        sessionStorage.setItem(`${storageKeyPrefix}_key_${dedupeKey}`, eventId);
      }
    }
  } catch {
    // no-op
  }
};

const wasEventAlreadySent = (eventName, eventId, dedupeKey) => {
  try {
    const storageKeyPrefix = `meta_${String(eventName || "event").toLowerCase()}`;
    const eventStorageKey = `${storageKeyPrefix}:${eventId}`;

    if (typeof window !== "undefined" && window.__metaEventIds?.has(eventStorageKey)) {
      return true;
    }

    if (typeof sessionStorage !== "undefined") {
      if (sessionStorage.getItem(`${storageKeyPrefix}_event_${eventId}`)) return true;
      if (dedupeKey && sessionStorage.getItem(`${storageKeyPrefix}_key_${dedupeKey}`)) return true;
    }
  } catch {
    return false;
  }

  return false;
};

const generateEventId = (eventName = "lead") =>
  `${String(eventName || "lead").toLowerCase()}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const buildBrowserPayload = ({ value, currency, customData = {} } = {}) => {
  const payload = { ...customData };
  const numericValue = Number(value);
  const finalCurrency = String(currency || "").toUpperCase();
  const shouldSendValue = Number.isFinite(numericValue) && isIsoCurrency(finalCurrency);

  if (shouldSendValue) {
    payload.value = numericValue;
    payload.currency = finalCurrency;
  }

  return payload;
};

export const trackMetaEventHybrid = async ({
  eventName = "Lead",
  email,
  phone,
  value,
  currency,
  eventSourceUrl = "/thank-you",
  eventId,
  dedupeKey,
  customData = {},
  userData = {},
  originalEventData = {},
  attributionData = {},
  actionSource = "website",
  sendBrowserEvent = true,
} = {}) => {
  if (typeof window === "undefined") {
    return { sent: false, reason: "ssr" };
  }

  const hostname = window.location.hostname;
  if (!isAllowedMetaHostname(hostname)) {
    return { sent: false, reason: "blocked-hostname", hostname };
  }

  const normalizedEventName = normalizeMetaEventName(eventName);
  const finalEventId = eventId || generateEventId(normalizedEventName);

  if (wasEventAlreadySent(normalizedEventName, finalEventId, dedupeKey)) {
    return { sent: false, reason: "duplicate", eventId: finalEventId };
  }

  const browserPayload = buildBrowserPayload({ value, currency, customData });
  markEventSent(normalizedEventName, finalEventId, dedupeKey);

  if (sendBrowserEvent && typeof window.fbq === "function") {
    window.fbq("track", normalizedEventName, browserPayload, { eventID: finalEventId });
  }

  const capiEndpoint = buildCapiEndpoint();
  if (!capiEndpoint) {
    return { sent: true, eventId: finalEventId, capiSent: false };
  }

  const capiPayload = {
    eventName: normalizedEventName,
    eventTime: Math.floor(Date.now() / 1000),
    eventSourceUrl: normalizeEventSourceUrl(eventSourceUrl),
    actionSource,
    eventId: finalEventId,
    userData: {
      ...userData,
      email: email ? String(email).trim().toLowerCase() : undefined,
      phone: phone ? normalizePhone(phone) : undefined,
      clientUserAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      fbp: getCookieValue("_fbp") || undefined,
      fbc: getCookieValue("_fbc") || undefined,
    },
    customData: browserPayload,
    originalEventData,
    attributionData,
  };

  try {
    await fetch(capiEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(capiPayload),
      keepalive: true,
    });
  } catch (error) {
    console.error("Meta CAPI send failed:", error);
  }

  return { sent: true, eventId: finalEventId, capiSent: true };
};

export const trackLeadHybrid = async ({
  email,
  phone,
  value = DEFAULT_LEAD_VALUE,
  currency = DEFAULT_LEAD_CURRENCY,
  eventSourceUrl = "/thank-you",
  eventId,
  dedupeKey,
  customData = {},
  userData = {},
  sendBrowserEvent = true,
} = {}) =>
  trackMetaEventHybrid({
    eventName: "Lead",
    email,
    phone,
    value,
    currency,
    eventSourceUrl,
    eventId,
    dedupeKey,
    customData,
    userData,
    sendBrowserEvent,
  });
